import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RiEditLine, RiSaveLine, RiCloseLine, RiAddLine, RiDeleteBinLine, RiImageAddLine } from 'react-icons/ri';
import toast from 'react-hot-toast';

const BarRestaurantsContents = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  // Fetch content
  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/bar-restaurants-contents/get-contents`
      );
      setContent(data);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  // Update content
  const updateContent = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${process.env.REACT_APP_API_URL}/bar-restaurants-contents/update-contents`,
        content
      );
      toast.success('Content updated successfully');
      setEditingSection(null);
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  // Cloudinary image upload
  const handleImageUpload = async (file, fieldPath) => {
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

      const response = await axios.post(
        process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
        uploadData
      );

      // Update the specific image field in content
      updateNestedField(fieldPath, response.data.secure_url);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
    }
  };

  // Helper to update nested fields
  const updateNestedField = (path, value) => {
    const keys = path.split('.');
    setContent(prev => {
      const newContent = { ...prev };
      let current = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newContent;
    });
  };

  // Handle text input changes
  const handleTextChange = (path, value) => {
    updateNestedField(path, value);
  };

  // Handle array item changes
  const handleArrayItemChange = (path, index, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      const array = path.split('.').reduce((obj, key) => obj[key], newContent);
      array[index][field] = value;
      return newContent;
    });
  };

  // Add new array item
  const addArrayItem = (path, template) => {
    setContent(prev => {
      const newContent = { ...prev };
      const array = path.split('.').reduce((obj, key) => obj[key], newContent);
      array.push({ ...template, _id: Date.now().toString() });
      return newContent;
    });
  };

  // Remove array item
  const removeArrayItem = (path, index) => {
    setContent(prev => {
      const newContent = { ...prev };
      const array = path.split('.').reduce((obj, key) => obj[key], newContent);
      array.splice(index, 1);
      return newContent;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-lg mb-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-transparent p-6">
        <div className="text-center py-12">
          <p className="text-secondaryTextColor text-lg">No content found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent p-4 lg:p-6">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
            Bar & Restaurant Content
          </h1>
          <p className="text-secondaryTextColor mt-2">
            Manage all content for the Bar & Restaurant page
          </p>
        </div>
        <button
          onClick={updateContent}
          disabled={saving}
          className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-3 px-6 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center"
        >
          <RiSaveLine className="mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {/* Hero Section */}
      <SectionWrapper
        title="Hero Section"
        isEditing={editingSection === 'hero'}
        onEdit={() => setEditingSection('hero')}
        onCancel={() => setEditingSection(null)}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EditableField
            label="Title"
            value={content.hero.title}
            editing={editingSection === 'hero'}
            onChange={(value) => handleTextChange('hero.title', value)}
            type="text"
          />
          <EditableField
            label="Subtitle"
            value={content.hero.subtitle}
            editing={editingSection === 'hero'}
            onChange={(value) => handleTextChange('hero.subtitle', value)}
            type="textarea"
          />
        </div>
      </SectionWrapper>

      {/* Fuel Up Section */}
      <SectionWrapper
        title="Fuel Up Section"
        isEditing={editingSection === 'fuelUp'}
        onEdit={() => setEditingSection('fuelUp')}
        onCancel={() => setEditingSection(null)}
      >
        <div className="space-y-6">
          <EditableField
            label="Title"
            value={content.fuelUpSection.title}
            editing={editingSection === 'fuelUp'}
            onChange={(value) => handleTextChange('fuelUpSection.title', value)}
            type="text"
          />
          <EditableField
            label="Description"
            value={content.fuelUpSection.description}
            editing={editingSection === 'fuelUp'}
            onChange={(value) => handleTextChange('fuelUpSection.description', value)}
            type="textarea"
          />
          
          {/* Highlights */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-primaryTextColor">Highlights</h4>
              {editingSection === 'fuelUp' && (
                <button
                  onClick={() => addArrayItem('fuelUpSection.highlights', {
                    icon: '⭐',
                    title: 'New Highlight',
                    description: 'Description here'
                  })}
                  className="flex items-center text-sm text-primaryBtnColor"
                >
                  <RiAddLine className="mr-1" />
                  Add Highlight
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {content.fuelUpSection.highlights.map((highlight, index) => (
                <div key={highlight._id} className="bg-gray-50 rounded-lg p-4">
                  {editingSection === 'fuelUp' ? (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <input
                          type="text"
                          value={highlight.icon}
                          onChange={(e) => handleArrayItemChange('fuelUpSection.highlights', index, 'icon', e.target.value)}
                          className="w-20 border border-gray-300 rounded p-2"
                          placeholder="Icon"
                        />
                        <button
                          onClick={() => removeArrayItem('fuelUpSection.highlights', index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <RiDeleteBinLine />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={highlight.title}
                        onChange={(e) => handleArrayItemChange('fuelUpSection.highlights', index, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded p-2"
                        placeholder="Title"
                      />
                      <textarea
                        value={highlight.description}
                        onChange={(e) => handleArrayItemChange('fuelUpSection.highlights', index, 'description', e.target.value)}
                        className="w-full border border-gray-300 rounded p-2"
                        placeholder="Description"
                        rows="2"
                      />
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-2xl mb-2">{highlight.icon}</div>
                      <h5 className="font-semibold text-primaryTextColor">{highlight.title}</h5>
                      <p className="text-secondaryTextColor text-sm">{highlight.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bullet Points */}
          <EditableArrayField
            label="Bullet Points"
            items={content.fuelUpSection.bulletPoints}
            editing={editingSection === 'fuelUp'}
            onItemsChange={(items) => handleTextChange('fuelUpSection.bulletPoints', items)}
          />

          <EditableField
            label="Note"
            value={content.fuelUpSection.note}
            editing={editingSection === 'fuelUp'}
            onChange={(value) => handleTextChange('fuelUpSection.note', value)}
            type="text"
          />
        </div>
      </SectionWrapper>

      {/* Eat Drink Play Section */}
      <SectionWrapper
        title="Eat Drink Play Section"
        isEditing={editingSection === 'eatDrinkPlay'}
        onEdit={() => setEditingSection('eatDrinkPlay')}
        onCancel={() => setEditingSection(null)}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <EditableField
                label="Title"
                value={content.eatDrinkPlay.title}
                editing={editingSection === 'eatDrinkPlay'}
                onChange={(value) => handleTextChange('eatDrinkPlay.title', value)}
                type="text"
              />
              <EditableField
                label="Description"
                value={content.eatDrinkPlay.description}
                editing={editingSection === 'eatDrinkPlay'}
                onChange={(value) => handleTextChange('eatDrinkPlay.description', value)}
                type="textarea"
              />
              <EditableArrayField
                label="Tags"
                items={content.eatDrinkPlay.tags}
                editing={editingSection === 'eatDrinkPlay'}
                onItemsChange={(items) => handleTextChange('eatDrinkPlay.tags', items)}
              />
            </div>
            <ImageUploader
              label="Section Image"
              imageUrl={content.eatDrinkPlay.image}
              editing={editingSection === 'eatDrinkPlay'}
              onImageUpload={(file) => handleImageUpload(file, 'eatDrinkPlay.image')}
            />
          </div>
        </div>
      </SectionWrapper>

      {/* Big Screen Section */}
      <SectionWrapper
        title="Big Screen Section"
        isEditing={editingSection === 'bigScreen'}
        onEdit={() => setEditingSection('bigScreen')}
        onCancel={() => setEditingSection(null)}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <EditableField
              label="Title"
              value={content.bigScreen.title}
              editing={editingSection === 'bigScreen'}
              onChange={(value) => handleTextChange('bigScreen.title', value)}
              type="text"
            />
            <EditableField
              label="Subtitle"
              value={content.bigScreen.subtitle}
              editing={editingSection === 'bigScreen'}
              onChange={(value) => handleTextChange('bigScreen.subtitle', value)}
              type="text"
            />
          </div>
          
          <EditableField
            label="Description"
            value={content.bigScreen.description}
            editing={editingSection === 'bigScreen'}
            onChange={(value) => handleTextChange('bigScreen.description', value)}
            type="textarea"
          />

          {/* Features */}
          <EditableObjectArray
            label="Features"
            items={content.bigScreen.features}
            editing={editingSection === 'bigScreen'}
            onItemsChange={(items) => handleTextChange('bigScreen.features', items)}
            fields={[
              { key: 'label', label: 'Label', type: 'text' },
              { key: 'value', label: 'Value', type: 'text' }
            ]}
          />

          {/* Stats */}
          <EditableObjectArray
            label="Stats"
            items={content.bigScreen.stats}
            editing={editingSection === 'bigScreen'}
            onItemsChange={(items) => handleTextChange('bigScreen.stats', items)}
            fields={[
              { key: 'label', label: 'Label', type: 'text' },
              { key: 'value', label: 'Value', type: 'text' }
            ]}
          />

          {/* Bottom Features */}
          <EditableObjectArray
            label="Bottom Features"
            items={content.bigScreen.bottomFeatures}
            editing={editingSection === 'bigScreen'}
            onItemsChange={(items) => handleTextChange('bigScreen.bottomFeatures', items)}
            fields={[
              { key: 'icon', label: 'Icon', type: 'text' },
              { key: 'label', label: 'Label', type: 'text' }
            ]}
          />
        </div>
      </SectionWrapper>

      {/* Menus Section */}
      <SectionWrapper
        title="Menus"
        isEditing={editingSection === 'menus'}
        onEdit={() => setEditingSection('menus')}
        onCancel={() => setEditingSection(null)}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* BBQ Menu */}
          <div className="space-y-4">
            <h4 className="font-semibold text-primaryTextColor">BBQ Menu</h4>
            <EditableField
              label="Title"
              value={content.menus.bbqMenu.title}
              editing={editingSection === 'menus'}
              onChange={(value) => handleTextChange('menus.bbqMenu.title', value)}
              type="text"
            />
            <EditableField
              label="Subtitle"
              value={content.menus.bbqMenu.subtitle}
              editing={editingSection === 'menus'}
              onChange={(value) => handleTextChange('menus.bbqMenu.subtitle', value)}
              type="text"
            />
            <EditableArrayField
              label="Features"
              items={content.menus.bbqMenu.features}
              editing={editingSection === 'menus'}
              onItemsChange={(items) => handleTextChange('menus.bbqMenu.features', items)}
            />
            <ImageUploader
              label="BBQ Menu Image"
              imageUrl={content.menus.bbqMenu.image}
              editing={editingSection === 'menus'}
              onImageUpload={(file) => handleImageUpload(file, 'menus.bbqMenu.image')}
            />
          </div>

          {/* Full Menu */}
          <div className="space-y-4">
            <h4 className="font-semibold text-primaryTextColor">Full Menu</h4>
            <EditableField
              label="Title"
              value={content.menus.fullMenu.title}
              editing={editingSection === 'menus'}
              onChange={(value) => handleTextChange('menus.fullMenu.title', value)}
              type="text"
            />
            <EditableField
              label="Subtitle"
              value={content.menus.fullMenu.subtitle}
              editing={editingSection === 'menus'}
              onChange={(value) => handleTextChange('menus.fullMenu.subtitle', value)}
              type="text"
            />
            <EditableArrayField
              label="Features"
              items={content.menus.fullMenu.features}
              editing={editingSection === 'menus'}
              onItemsChange={(items) => handleTextChange('menus.fullMenu.features', items)}
            />
            <ImageUploader
              label="Full Menu Image"
              imageUrl={content.menus.fullMenu.image}
              editing={editingSection === 'menus'}
              onImageUpload={(file) => handleImageUpload(file, 'menus.fullMenu.image')}
            />
          </div>
        </div>
      </SectionWrapper>

      {/* Feature Icons */}
      <SectionWrapper
        title="Feature Icons"
        isEditing={editingSection === 'featureIcons'}
        onEdit={() => setEditingSection('featureIcons')}
        onCancel={() => setEditingSection(null)}
      >
        <EditableObjectArray
          items={content.featureIcons}
          editing={editingSection === 'featureIcons'}
          onItemsChange={(items) => handleTextChange('featureIcons', items)}
          fields={[
            { key: 'icon', label: 'Icon', type: 'text' },
            { key: 'label', label: 'Label', type: 'text' }
          ]}
        />
      </SectionWrapper>
    </div>
  );
};

// Reusable Components
const SectionWrapper = ({ title, children, isEditing, onEdit, onCancel }) => (
  <div className="bg-white rounded-2xl shadow-lg mb-6 overflow-hidden">
    <div className="flex justify-between items-center p-6 border-b border-gray-200">
      <h3 className="text-xl font-bold text-primaryTextColor">{title}</h3>
      <div>
        {!isEditing ? (
          <button
            onClick={onEdit}
            className="flex items-center text-primaryBtnColor hover:text-primaryBtnHoverColor"
          >
            <RiEditLine className="mr-2" />
            Edit
          </button>
        ) : (
          <button
            onClick={onCancel}
            className="flex items-center text-secondaryTextColor hover:text-primaryTextColor"
          >
            <RiCloseLine className="mr-2" />
            Cancel
          </button>
        )}
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const EditableField = ({ label, value, editing, onChange, type = 'text' }) => (
  <div>
    <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
      {label}
    </label>
    {editing ? (
      type === 'textarea' ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor resize-none"
          rows="3"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
        />
      )
    ) : (
      <p className={`p-3 rounded-lg ${type === 'textarea' ? 'bg-gray-50' : ''}`}>
        {value}
      </p>
    )}
  </div>
);

const EditableArrayField = ({ label, items, editing, onItemsChange }) => (
  <div>
    <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
      {label}
    </label>
    {editing ? (
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = e.target.value;
                onItemsChange(newItems);
              }}
              className="flex-1 border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
            />
            <button
              onClick={() => {
                const newItems = items.filter((_, i) => i !== index);
                onItemsChange(newItems);
              }}
              className="text-red-500 hover:text-red-700 p-2"
            >
              <RiDeleteBinLine />
            </button>
          </div>
        ))}
        <button
          onClick={() => onItemsChange([...items, ''])}
          className="flex items-center text-sm text-primaryBtnColor"
        >
          <RiAddLine className="mr-1" />
          Add Item
        </button>
      </div>
    ) : (
      <div className="space-y-1">
        {items.map((item, index) => (
          <p key={index} className="p-2 bg-gray-50 rounded-lg">• {item}</p>
        ))}
      </div>
    )}
  </div>
);

const EditableObjectArray = ({ label, items, editing, onItemsChange, fields }) => (
  <div>
    {label && (
      <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
        {label}
      </label>
    )}
    {editing ? (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item._id} className="border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs text-secondaryTextColor mb-1">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    value={item[field.key]}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[index][field.key] = e.target.value;
                      onItemsChange(newItems);
                    }}
                    className="w-full border border-gray-300 rounded p-2 text-sm"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const newItems = items.filter((_, i) => i !== index);
                onItemsChange(newItems);
              }}
              className="mt-2 text-red-500 hover:text-red-700 text-sm flex items-center"
            >
              <RiDeleteBinLine className="mr-1" />
              Remove
            </button>
          </div>
        ))}
        <button
          onClick={() => {
            const newItem = fields.reduce((acc, field) => {
              acc[field.key] = '';
              return acc;
            }, { _id: Date.now().toString() });
            onItemsChange([...items, newItem]);
          }}
          className="flex items-center text-sm text-primaryBtnColor"
        >
          <RiAddLine className="mr-1" />
          Add Item
        </button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map((item, index) => (
          <div key={item._id} className="text-center p-4 bg-gray-50 rounded-lg">
            {item.icon && <div className="text-2xl mb-2">{item.icon}</div>}
            {item.label && <div className="font-semibold text-primaryTextColor">{item.label}</div>}
            {item.value && <div className="text-secondaryTextColor text-sm">{item.value}</div>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const ImageUploader = ({ label, imageUrl, editing, onImageUpload }) => (
  <div>
    <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
      {label}
    </label>
    {editing ? (
      <div className="flex flex-col items-center">
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg mb-3"
          />
        )}
        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primaryBtnColor transition-colors">
          <RiImageAddLine className="text-2xl text-gray-400 mb-2" />
          <span className="text-sm text-gray-500">Click to upload image</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) onImageUpload(file);
            }}
            className="hidden"
          />
        </label>
      </div>
    ) : (
      imageUrl && (
        <img
          src={imageUrl}
          alt={label}
          className="w-full h-48 object-cover rounded-lg"
        />
      )
    )}
  </div>
);

export default BarRestaurantsContents;